import {type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import * as BsIcons from "react-icons/bs";
import {Editor, type OnMount} from "@monaco-editor/react";
import * as monaco from 'monaco-editor';
import {cloud_init_api, CloudInitAPIConnectorError} from "../../lib/CloudInit.ts";
import {frontBaseUrl} from "../../build_constants.ts";
import {Navbar} from "../../components/Navbar.tsx";
import defaultCloutInitYaml from "../../assets/default-cloud-init.yaml?raw"
import {swal_util} from "../../lib/SwalUtil.ts";
import {instance_manager} from "../../lib/InstanceManager.ts";
import type {OSImage} from "../../lib/ModelTypes.ts";
import {ModifyYamlPreviewModal, type ModifyYamlPreviewModalRef} from "./ModifyYamlPreviewModal.tsx";
import {useTranslation} from "react-i18next";

interface ContentProps {
    toggleSidebar: () => void;
}

// other view
// const data = await cloud_init_api.getFileContents(name);
// navigate(`${frontBaseUrl}/cloud_init`, {state: {data: data!.data}});
// this view
// const locationData = useLocation();
// const receivedData = locationData.state as { data: YamlData } | null;
// useEffect(() => {
//     if (receivedData) {
//         setData((prev) => ({...prev, yamlName: receivedData.data.name}))
//     }
//     init().finally();
//
// }, [receivedData])

function insertTemplateCode(editor: monaco.editor.IStandaloneCodeEditor, selectedTemplateCode: string) {
    const selection = editor.getSelection();
    if (selection) {
        editor.executeEdits(
            "",
            [
                {
                    range: selection,
                    text: selectedTemplateCode,
                    forceMoveMarkers: true,
                },
            ]
        );
    } else {
        console.warn("No selection to insert text.");
    }
}

const ModifyYaml = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation();
    const {name} = useParams<{ name?: string }>();
    const [content, setContent] = useState<string>("");

    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
    const modifyYamlPreviewModalRef = useRef<ModifyYamlPreviewModalRef | null>(null);

    const [selectedTemplateCode, setSelectedTemplateCode] = useState("${name}");
    const navigate = useNavigate();

    const [osImages, setOsImages] = useState<OSImage[]>([]);

    const loadYamlData = useCallback(async () => {
        if (!name) {
            return defaultCloutInitYaml.replace("#cloud-config", `#cloud-config\n##create: ${new Date().toISOString()}`)
        }
        const res = await cloud_init_api.getFileContents(name);
        const jsonData = res!.data;
        return jsonData.data;
    }, [name]);


    useEffect(() => {
        if (osImages.length === 0) {
            instance_manager.prepare().then(() => {
                setOsImages(instance_manager.osImages);
            });

        }

        loadYamlData().then((content) => {
            setContent(content);
        });
    }, [loadYamlData, osImages]);

    // useEffect(() => {

    // });

    const handleEditorDidMount: OnMount = (editor) => {
        editorRef.current = editor;
    };


    function openPreviewModal() {
        modifyYamlPreviewModalRef.current?.show();
    }


    async function onSave(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const editor = editorRef.current

        if (!editor) return

        const formData = new FormData(e.currentTarget);
        const newName = formData.get("newName") as string;
        const content = editor.getValue();

        if (!newName) {
            await swal_util.show_warning(t("CloudInit.NoScriptTitle"));
            return false;
        }
        if (!content.trim()) {
            await swal_util.show_warning(t("CloudInit.emptyScriptContent"));
            return false;
        }

        try {
            if (!name) {
                await cloud_init_api.generate(newName, content);
            } else {
                await cloud_init_api.modify(name, newName, content);
            }

            navigate(`${frontBaseUrl}/cloud_init`);
        } catch (err) {
            if (err instanceof CloudInitAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(`${t("CloudInit.errorEditYaml")} ${t("common.adminContact")}`);
            }
            console.log(err);
        }
    }

    function onSelectedTemplateCodeChange(e: ChangeEvent<HTMLSelectElement>) {
        const selectValue = e.target.value;
        setSelectedTemplateCode(selectValue);
    }

    function onClickInsertEvent() {
        if (!editorRef.current) return;
        insertTemplateCode(editorRef.current, selectedTemplateCode);
        editorRef.current.focus();
    }


    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>
            <article className="blueshift_main" id="wrapper">
                <div className="p-4 pt-3 pb-0">
                    <div className="row">
                        <Link to={`${frontBaseUrl}/cloud_init`} className="text-decoration-none text-black">
                            <BsIcons.BsArrowLeft size="17" className="mb-1 ms-1"/>
                            &ensp;{t("common.back")}
                        </Link>
                    </div>
                    <form className="card-body mx-auto mt-3" style={{width: "100%"}} onSubmit={onSave}>
                        <div className="row">
                            <div id="left-context" className="col-4">
                                <div className="form-group px-3">
                                    <div className="px-0" style={{width: "fit-content"}}>
                                        <h4 className="text-primary text-end pt-2 mb-4">{t("common.name")}</h4>
                                    </div>
                                    <div className="pe-0">
                                        <input
                                            name="newName" type="text"
                                            className="form-control"
                                            defaultValue={name}
                                        />
                                    </div>
                                </div>

                                <div className="form-group px-3 mb-3">
                                    <div className="px-0" style={{width: "fit-content"}}>
                                        <h4 className="text-primary pt-2">{t("CloudInit.YamlTemplateCode")}</h4>
                                    </div>
                                    <div className="row me-1">
                                        <div className="col-8">
                                            <select id="template-code" name="code"
                                                    onChange={onSelectedTemplateCodeChange}
                                                    value={selectedTemplateCode}
                                                    className="form-select">

                                                <optgroup label={t("common.osInfo")} />
                                                <option value="${name}">{t("common.osUserName")}</option>
                                                <option value="${os_name}">{t("common.osName")}</option>

                                                <optgroup label={t("common.blueshiftInstance")} />
                                                <option value="${instance_name}">{t("instance.instanceName")}</option>

                                                <optgroup label={t("common.blueshiftUserAccount")}/>
                                                <option value="${user.id}">{t("common.userID")}</option>
                                                <option value="${user.email}">{t("common.userEmail")}</option>
                                            </select>
                                        </div>
                                        <button className="button_blue col-4 py-1 rounded-3" style={{float: "left"}}
                                                onClick={onClickInsertEvent} type="button">{t("common.add")}
                                        </button>
                                    </div>
                                </div>
                                <div className="card-footer mx-3 mt-2 text-center">
                                    <button className="button_blue py-1 rounded-3 col-5 me-2" type="button"
                                            onClick={openPreviewModal}>{t("common.preview")}
                                    </button>
                                    <button className="button_blue py-1 rounded-3 col-5 me-2" type="submit">{t("common.save")}</button>
                                </div>
                            </div>
                            <div id="right-context" className="col-8">
                                <Editor
                                    className="code-editor"
                                    defaultLanguage="yaml"
                                    value={content}
                                    onChange={(newValue) => setContent(newValue!)}
                                    onMount={handleEditorDidMount}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </article>
            <ModifyYamlPreviewModal osImages={osImages} ref={modifyYamlPreviewModalRef} content={content}/>
        </main>
    );
}

export default ModifyYaml;